import { ChangeDetectorRef, Component, inject, OnInit, Pipe, PipeTransform } from '@angular/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MessageApiService, Notice } from '../../message.api.service';
import { NoticeComponent } from '../notice/notice.component';

export interface NoticeDateGroup {
  /** 日期分割线文案，如 `8月1日` */
  dayLabel: string;
  /** 该日期下的通知 */
  notices: Notice[];
}

/**
 * 通知按天分组 Pipe：按 createAt 的天分组，同一日期聚合为一段，输出日期分割线文案。
 */
@Pipe({
  name: 'noticeDateGroup',
  standalone: true,
})
export class NoticeDateGroupPipe implements PipeTransform {
  transform(notices: Notice[] | undefined): NoticeDateGroup[] {
    if (!notices || notices.length === 0) return [];

    const map = new Map<string, Notice[]>();
    for (const n of notices) {
      const key = this.dayKey(n.createAt);
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(n);
      map.set(key, list);
    }

    const groups: NoticeDateGroup[] = [];
    for (const [key, list] of map) {
      groups.push({ dayLabel: key, notices: list });
    }
    return groups;
  }

  /** 提取 `M月d日` 日期文案（不补前导零）；无法解析返回 '' */
  private dayKey(createAt: Date | string | undefined): string {
    if (!createAt) return '';
    const d = new Date(createAt);
    if (isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  }
}

/**
 * 通知列表组件（系统消息列表，滚动加载分页）
 *
 * 领域模型见 message/README.md：
 * - 数据来自 MessageApiService.getNotice(page) 分页接口，滚动到底自动加载下一页
 * - 按 createAt 天分组，每组前渲染日期分割线（M月d日），组内渲染 fl-notice 单条通知
 * - 由 message-panel 在左侧列表某项 type===0（系统消息）点击后渲染
 */
@Component({
  selector: 'fl-notice-list',
  templateUrl: './notice-list.component.html',
  styleUrls: ['./notice-list.component.scss'],
  standalone: true,
  imports: [NzSpinModule, NoticeComponent, NoticeDateGroupPipe],
})
export class NoticeListComponent implements OnInit {
  /** 累积的通知列表（跨页追加） */
  notices: Notice[] = [];

  /** 当前页 */
  private page = 1;

  /** 总条数 */
  total = 0;

  /** 加载中 */
  loading = false;

  /** 已到达最底部（触发滚动加载后再重置） */
  private reachedBottom = false;

  /** 系统通知首屏获取成功后即视为全部已读 */
  private systemRead = false;

  private messageApiSrv = inject(MessageApiService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit() {
    this.loadNotices();
  }

  /** 加载下一页，累积追加 */
  async loadNotices() {
    if (this.loading || this.reachedBottom) return;
    this.loading = true;
    try {
      const result = await this.messageApiSrv.getNotice(this.page);
      if (this.page === 1 && !this.systemRead) {
        this.systemRead = true;
        this.messageApiSrv.consumeSystemUnread();
      }
      // 去重：同一通知跨页重复时避免重复渲染
      const existing = new Set(this.notices.map(n => n.id));
      const fresh = result.results
        .filter(n => !existing.has(n.id))
        .map(notice => (this.systemRead ? { ...notice, hasRead: true } : notice));
      this.notices = this.notices.concat(fresh);
      this.total = result.count;
      this.page += 1;
      if (this.notices.length >= this.total) {
        this.reachedBottom = true;
      }
    } finally {
      this.loading = false;
    }
    // OnPush：await 续体非模板事件，通知列表/加载态需主动标记
    this.cdr.markForCheck();
  }

  /**
   * 容器滚动到底部时加载下一页
   */
  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gap < 40) {
      this.loadNotices();
    }
  }
}
